import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {gofor, safe} from '@fiverr-private/futile';
import pathfinder from '@fiverr-private/pathfinder';
import Modal from '@fiverr/modal';
import SocialMediaBody from './components/SocialMediaBody';
import CloseButton from './components/CloseButton';
import '@fiverr/modal/dist/index.css';
import translations from './translations.en.json';
import '@fiverr/i18n/singleton';
import {sendClientEvents} from './utils/bigQueryReporter';
import {logEventToGraphite} from './utils/graphiteReporter';
import {platforms, POPUP_CONFIG} from './utils/constants';
import {redirectShareLink} from './utils/redirectShareLink';
import {copyLink} from './utils/copyLink';
import EmailBody from './components/EmailBody';
import BackButton from './components/BackButton';
import sendMixPanelEvent from './utils/mixPanelReporter';

import './index.scss';

class SharingLink extends PureComponent {

    static get boundFunctions() {
        return [
            'onShareEventCalled',
            'openModal',
            'onClose',
            'onSocialMediumClick',
            'fetchLink',
            'setShareable',
            'resetMediaState',
            'closeModal',
            'finaliseShare',
            'setPropertiesOnMedium',
            'getSourceTranslationKey',
            'onModalOpen',
            'findSocialMedium',
            'defineCopyLink',
            'onModalClose',
            'onSendMail',
            'onBack'
        ];
    }

    constructor(props) {
        super(props);

        this.constructor.boundFunctions.forEach((fn) => this[fn] = this[fn].bind(this));
        this._modalInterface = {};

        this.state = {
            translationKey: '', // current translation path for this source
            socialMedia: [], // stores Social platforms visual state
            settings: { // stores Modal settings
                title: '',
                description: [],
                email_title: ''
            },
            shareable: null, // the object to share (gig / delivery / user)
            isLoading: true,
            showMail: false
        };
    }

    componentDidMount() {
        if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
            i18n.add(translations);
            window.addEventListener('open-share-modal', this.onShareEventCalled, false);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('open-share-modal', this.onShareEventCalled, false);
    }

    onShareEventCalled(e) {
        if (this.isValidSource(e.detail)) {
            sendClientEvents('social_share.button.click');
            this.setShareable(e.detail, true);
            return;
        }

        throw new Error('Please make sure all the required fields are present.');
    }

    isValidSource(data) {
        if (typeof data !== 'object') {
            return false;
        }

        return (
            data.hasOwnProperty('source') &&
            data.hasOwnProperty('resource_type') &&
            data.hasOwnProperty('resource_id') &&
            data.hasOwnProperty('is_resource_owner')
        );
    }

    onClose() {
        this._modalInterface.close();
    }

    onBack() {
        this.setState({
            showMail: false
        });
    }

    onModalOpen() {
        const copyLinkMedium = this.findSocialMedium(platforms.COPY_LINK);
        if (copyLinkMedium && !copyLinkMedium.link) {
            this.fetchLink(platforms.COPY_LINK, this.defineCopyLink);
        }
    }

    onModalClose() {
        this.resetMediaState();
    }

    onSendMail() {
        this.onBack();
    }

    defineCopyLink(medium, shareLink) {
        this.setPropertiesOnMedium(platforms.COPY_LINK, {isLoading: false, isVisited: false, link: shareLink});
    }

    findSocialMedium(medium) {
        return this.state.socialMedia.find((item) => (item.medium === medium));
    }

    onSocialMediumClick(medium) {
        const {translationKey, shareable} = this.state,
            vars = _.result(shareable, 'interpolation_variables', {}),
            shareText = i18n.t(`${translationKey}.share_texts.${medium}`) !== medium ? i18n.t(`${translationKey}.share_texts.${medium}`, vars) : i18n.t(`${translationKey}.share_texts.default`, vars);

        sendClientEvents('social_share.click', medium);
        sendMixPanelEvent(medium, this.state.shareable.resource_type);
        this.setPropertiesOnMedium(medium, {isVisited: true});

        switch (medium) {
            case platforms.COPY_LINK:
                copyLink(this.findSocialMedium(platforms.COPY_LINK).link);
                this.finaliseShare(medium);
                break;
            case platforms.EMAIL:
                this.setState({
                    showMail: true
                });
                break;
            case platforms.WHATSAPP:
                window.open(redirectShareLink(medium, shareable, shareText), '_blank');
                this.finaliseShare(medium);
                break;
            default:
                window.open(redirectShareLink(medium, shareable, shareText), 'sharepopup', POPUP_CONFIG);
                this.finaliseShare(medium);
                break;
        }
    }

    setShareable(shareable, openAfterSetup = false) {
        if (JSON.stringify(shareable) === JSON.stringify(this.state.shareable)) { // check if the same item clicked again
            this.openModal();
            return;
        }

        const {source, interpolation_variables} = shareable,
            translationKey = this.getSourceTranslationKey(source),
            newSocialMedia = i18n.t(`${translationKey}.mediums`).map((medium) => Object.assign({}, {medium, isLoading: false})),
            settings = this.state.settings;

        for (const settingKey in settings) {
            if (Array.isArray(i18n.t(`${translationKey}.${settingKey}`))) {
                settings[settingKey] = Object.keys(i18n.t(`${translationKey}.${settingKey}`)).map((key) => i18n.t(`${translationKey}.${settingKey}.${key}`, interpolation_variables));
            } else {
                settings[settingKey] = i18n.t(`${translationKey}.${settingKey}`, interpolation_variables);
            }
        }

        this.setState({
            translationKey,
            shareable,
            settings,
            socialMedia: newSocialMedia,
            isLoading: false
        }, () => openAfterSetup ? this.openModal() : null);
    }

    openModal() {
        if (this._modalInterface && typeof this._modalInterface.open === 'function') {

            this._modalInterface.shouldPreventCloseAction = () => true;

            /**
             * The "shouldPreventCloseAction" function is part of the toggler.
             * once this function returns false, the modal will not close.
             * Our issue happens once we trigger the custom event through an outside click event,
             * this event will cause the modal to close itself immediately, therefore, setting the
             * "shouldPreventCloseAction" to false until the end of the event loop fixes that problem.
            */
            setTimeout(() => this._modalInterface.shouldPreventCloseAction = () => false);

            this._modalInterface.open();
        }
    }

    getSourceTranslationKey(source) {
        const { ab_tests } = this.props;

        // get the default settings if
        // nothing else available
        if (source in translations.sources) {
            // check if we have any A/B tests running
            const sourceTestKey = Object.keys(translations.sources[source]).find((sourceKey) => sourceKey !== 'default');

            if (sourceTestKey && ab_tests && typeof ab_tests === 'object') {
                const transTestKey = `sources.${source}.${sourceTestKey}.${ab_tests[sourceTestKey]}`;

                if (typeof translations.sources[source][sourceTestKey][ab_tests[sourceTestKey]] === 'object') {
                    return transTestKey;
                }
            }

            // Get the default source settings
            return `sources.${source}.default`;
        } else {
            return 'sources.defaults';
        }
    }

    fetchLink(medium, callback) {
        const {source, resource_type, resource_id, is_resource_owner} = this.state.shareable;

        this.setPropertiesOnMedium(medium, {isLoading: true});

        const requestBody = {
            medium,
            resource_type,
            resource_id,
            is_resource_owner
        };

        if (source) {
            requestBody.source = source;
        }

        gofor(pathfinder('sharing_generate_link'), {
            method: 'POST',
            body: JSON.stringify(requestBody)
        })
            .then((response) => response.json())
            .then((response) => {
                if (response) {
                    safe(callback(medium, response.link));
                }
            })
            .catch((err) => {
                logEventToGraphite({label: 'social_share.generate_link.error'});
                throw err;
            });
    }

    finaliseShare(medium) {
        if (medium !== platforms.COPY_LINK) {
            this.closeModal();
        } else {
            setTimeout(() => this.closeModal(), 3000);
        }
    }

    closeModal() {
        if (typeof this._modalInterface.close === 'function') {
            this._modalInterface.close();
        }
    }

    setPropertiesOnMedium(medium, properties) {
        const newSocialMedia = this.state.socialMedia.map((socialMedium) => {
            if (socialMedium.medium === medium) {
                Object.assign(socialMedium, {...properties});
            }
            return socialMedium;
        });

        this.setState({
            socialMedia: newSocialMedia
        });
    }

    resetMediaState() {
        const newSocialMedia = this.state.socialMedia
            .map((item) => Object.assign({}, {...item, isLoading: false, isVisited: false}));

        this.setState({
            socialMedia: newSocialMedia,
            showMail: false
        });
    }

    render() {
        const {settings, socialMedia, showMail} = this.state;
        return (
            <Modal ref={(ref) => this._modalInterface = ref} onOpen={this.onModalOpen} onClose={this.onModalClose}>
                {({ preventCloseAction }) => (
                    <div className="sharing-link" ref={preventCloseAction}>
                        <CloseButton onClick={this.onClose}/>
                        { showMail && <BackButton onClick={this.onBack}/> }
                        { showMail ? (
                            <EmailBody emailTitle={settings.email_title} onSendMail={this.onSendMail}/>
                        ) : (
                            <SocialMediaBody settings={settings} socialMediumItems={socialMedia}
                                onClick={this.onSocialMediumClick}/>
                        )}
                    </div>
                )}
            </Modal>
        );
    }
}

SharingLink.PropTypes = {
    ab_tests: PropTypes.object
};

SharingLink.defaultProps = {
    ab_tests: null
};

export default SharingLink;



// WEBPACK FOOTER //
// ./src/SharingLink.js